// deps
import React, { ReactElement, memo, useMemo, useState, useCallback } from "react";
import cx from "classnames";

//internal-deps
import { InputFieldv4, withStyles } from "@swiggy-private/core-ui";
import { SuperButton } from "@swiggy-private/dls-web-core/dist/components/SuperButton";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";

//constants
import { TEXTS as COMMON_TEXTS } from "@payment-ui/constants/index";
import { TEXTS as LINK_ACCOUNT_TEXTS } from "@payment-ui/constants/LinkAccount";

//test-ids
import { LINK_ACCOUNT_PHONE_NUMBER_TEST_IDS } from "@swiggy-private/payment-test-ids";

//helpers
import { isValidPhoneNumber } from "@payment-ui/helpers/Wallet";

//styles
import styles from "./LinkAccountPhoneNumber.scss";

//components
import Header from "./components/Header";

//defs
import { LinkAccountPhoneNumberProps } from "../LinkAccountPhoneNumber";

function LinkAccountPhoneNumber(props: LinkAccountPhoneNumberProps): ReactElement {
    const { onConfirmLinking, iconUrl, displayName, editableState, mobileNumber, setMobileNumber } = props;
    const [error, setError] = useState(false);
    const { isEditable, registeredMobileNumber } = editableState;

    const attrs = useMemo(
        () => ({
            maxLength: 10,
            type: "tel",
            name: "phoneNumberInput",
            id: "phoneNumberInput",
            "data-testid": LINK_ACCOUNT_PHONE_NUMBER_TEST_IDS.PHONE_NUMBER_INPUT,
        }),
        [],
    );
    const onInputChange = useCallback(
        (number: string): void => {
            setMobileNumber(number);
            setError(false);
        },
        [setMobileNumber],
    );

    const _onConfirmLinking = useCallback(
        (event: React.MouseEvent): void => {
            event.preventDefault();

            if (!isEditable) {
                onConfirmLinking(registeredMobileNumber);
            }

            if (!isValidPhoneNumber(mobileNumber)) {
                setError(true);
                return;
            }

            onConfirmLinking(mobileNumber);
        },
        [isEditable, onConfirmLinking, mobileNumber, registeredMobileNumber],
    );

    const submitCtaClass = cx(styles.linkPhoneNumberButton, {
        [styles.linkPhoneNumberButtonNonEditableFlow]: !isEditable,
    });

    const isSubmitButtonDisabled = useMemo(() => isEditable && mobileNumber.length < 10, [
        isEditable,
        mobileNumber.length,
    ]);

    return (
        <div data-testid={LINK_ACCOUNT_PHONE_NUMBER_TEST_IDS.CONTAINER} className={styles.container}>
            <Header displayName={displayName} iconUrl={iconUrl} editableState={editableState} />
            <div className={styles.linkPhoneNumber}>
                <Text
                    variant="V2.body3.neutral.medium"
                    color="text_Low_Emphasis"
                    className={styles.linkPhoneNumberSubtitle}
                    data-testid={LINK_ACCOUNT_PHONE_NUMBER_TEST_IDS.SUB_TITLE}
                >
                    {COMMON_TEXTS.LINK_MOBILE_NUMBER_SUBTITLE}
                </Text>
                {isEditable && (
                    <InputFieldv4
                        className={styles.linkPhoneNumberInput}
                        label={LINK_ACCOUNT_TEXTS.PHONENO_INPUT_LABEL}
                        onChange={onInputChange}
                        attrs={attrs}
                        error={error}
                        value={mobileNumber}
                        errorMessage={error ? LINK_ACCOUNT_TEXTS.PHONENO_INPUT_ERROR_MESSAGE : undefined}
                        keepLabelActive={!!mobileNumber.length}
                    />
                )}
                <SuperButton
                    className={submitCtaClass}
                    onClick={_onConfirmLinking}
                    data-testid={LINK_ACCOUNT_PHONE_NUMBER_TEST_IDS.CONFIRM_LINKING_BUTTON}
                    buttonType="primary"
                    buttonSize="large"
                    isDisabled={isSubmitButtonDisabled}
                >
                    {isEditable ? LINK_ACCOUNT_TEXTS.CONFIRM_AND_PROCEED : LINK_ACCOUNT_TEXTS.PROCEED}
                </SuperButton>
            </div>
        </div>
    );
}
export default memo(withStyles(LinkAccountPhoneNumber, styles));
